import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Star, ChevronLeft, ChevronRight, ExternalLink, Quote } from "lucide-react";
import { useI18n } from "@/i18n";

const GOOGLE_G = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 009 18z"/>
    <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 013.66 9c0-.59.1-1.16.29-1.7V4.97H.96A9 9 0 000 9c0 1.45.35 2.83.96 4.04l2.99-2.33z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 009 0 9 9 0 00.96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
  </svg>
);

function Stars({ value = 5 }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${value} sur 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < value ? "text-[#B8956A] fill-[#B8956A]" : "text-[#D9D3C5] fill-[#D9D3C5]"}
        />
      ))}
    </div>
  );
}

function formatDate(d, locale) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString(locale === "en" ? "en-GB" : "fr-FR", {
      month: "long", year: "numeric",
    });
  } catch {
    return d;
  }
}

export default function ReviewsCarousel({ className = "" }) {
  const [reviews, setReviews] = useState([]);
  const [googleUrl, setGoogleUrl] = useState("");
  const [idx, setIdx] = useState(0);
  const { lang } = useI18n();
  const trackRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/reviews/public?limit=5")
      .then((r) => {
        setReviews(r.data?.reviews || []);
        setGoogleUrl(r.data?.google_url || "");
      })
      .catch(() => {});
  }, []);

  const next = useCallback(() => setIdx((i) => (reviews.length ? (i + 1) % reviews.length : 0)), [reviews.length]);
  const prev = useCallback(() => setIdx((i) => (reviews.length ? (i - 1 + reviews.length) % reviews.length : 0)), [reviews.length]);

  // Auto-rotate every 6s, pause on hover
  useEffect(() => {
    if (reviews.length <= 1) return;
    const root = trackRef.current;
    const start = () => { timerRef.current = setInterval(next, 6000); };
    const stop = () => { if (timerRef.current) clearInterval(timerRef.current); };
    start();
    root?.addEventListener("mouseenter", stop);
    root?.addEventListener("mouseleave", start);
    return () => {
      stop();
      root?.removeEventListener("mouseenter", stop);
      root?.removeEventListener("mouseleave", start);
    };
  }, [reviews.length, next]);

  if (!reviews.length) return null;

  const totalRating = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  const moreLabel = lang === "en" ? "Read all reviews on Google" : "Lire tous les avis sur Google";

  return (
    <div data-testid="reviews-carousel" ref={trackRef} className={`relative ${className}`}>
      {/* Aggregate rating header */}
      <div className="flex flex-col items-center gap-2 mb-10">
        <div className="flex items-center gap-3">
          <span className="font-display text-4xl text-[#203240]">{totalRating.toFixed(1)}</span>
          <Stars value={Math.round(totalRating)} />
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[#697585] flex items-center gap-2">
          {GOOGLE_G}
          <span>{lang === "en" ? `Based on ${reviews.length} reviews` : `Sur ${reviews.length} avis sélectionnés`}</span>
        </p>
      </div>

      {/* Card */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${idx * 100}%)` }}
        >
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="min-w-full px-2 md:px-6"
            >
              <div className="bg-white border border-[#D9D3C5] p-8 md:p-12 max-w-3xl mx-auto relative">
                <Quote size={48} className="text-[#B8956A]/20 absolute top-6 left-6" aria-hidden="true" />
                <div className="relative">
                  <Stars value={r.rating || 5} />
                  <p className="mt-6 font-display text-xl md:text-2xl text-[#203240] leading-relaxed">
                    « {r.content} »
                  </p>
                  <div className="mt-8 pt-6 border-t border-[#D9D3C5] flex items-center justify-between gap-4">
                    <div>
                      <p className="font-display text-lg text-[#203240]">{r.author}</p>
                      <p className="text-xs text-[#697585] uppercase tracking-[0.18em] mt-1">
                        {formatDate(r.date, lang)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#697585]">
                      {r.source === "google" && GOOGLE_G}
                      <span className="uppercase tracking-[0.18em]">{r.source || "google"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {reviews.length > 1 && (
          <>
            <button
              type="button"
              data-testid="review-prev"
              onClick={prev}
              aria-label="Previous"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white border border-[#D9D3C5] text-[#203240] hover:text-[#B8956A] hover:border-[#B8956A] transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              data-testid="review-next"
              onClick={next}
              aria-label="Next"
              className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white border border-[#D9D3C5] text-[#203240] hover:text-[#B8956A] hover:border-[#B8956A] transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {reviews.length > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          {reviews.map((_, i) => (
            <button
              key={i}
              data-testid={`review-dot-${i}`}
              onClick={() => setIdx(i)}
              aria-label={`Avis ${i + 1}`}
              className={`h-[2px] transition-all ${i === idx ? "w-10 bg-[#B8956A]" : "w-5 bg-[#D9D3C5] hover:bg-[#B8956A]/50"}`}
            />
          ))}
        </div>
      )}

      {/* Link to all reviews */}
      {googleUrl && (
        <div className="mt-10 text-center">
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="btn-ghost"
          >
            {GOOGLE_G} {moreLabel} <ExternalLink size={14} />
          </a>
        </div>
      )}
    </div>
  );
}
